import React, { useEffect, useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '@/lib/utils';

const LanguageSelector = ({ compact = false, iconOnly = false }) => {
  const { lang, setLang, LANGUAGES } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = LANGUAGES.find(l => l.code === lang);

  useEffect(() => {
    const onPointerDown = event => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) setOpen(false);
    };
    const onEsc = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  return (
    <div ref={rootRef} className="relative z-[1000] shrink-0">
      <button
        type="button"
        aria-label={`Language: ${current?.native || 'English'}`}
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-[10px] border border-de-gold/35 bg-de-gold/10 font-semibold text-de-gold transition-all duration-200',
          iconOnly ? 'h-10 w-10 justify-center px-0 py-0' : compact ? 'px-3 py-1.5 text-[11px]' : 'px-4 py-2 text-[13px]',
        )}
        onClick={() => setOpen(!open)}
      >
        <Globe className={iconOnly ? 'h-4 w-4' : 'h-3.5 w-3.5'} strokeWidth={1.75} />
        {!iconOnly && (
          <>
            <span>{current?.native}</span>
            <span className="text-[10px] opacity-70">{open ? '▲' : '▼'}</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-[1001] max-h-[min(400px,70dvh)] w-[min(300px,calc(100vw-24px))] overflow-y-auto rounded-xl border border-de-gold/30 bg-de-drawer p-2 shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
          <p className="mx-2 mb-2 mt-1 text-center text-[10px] tracking-wide text-de-gold/60">
            🇮🇳 SELECT YOUR LANGUAGE / भाषा चुनें
          </p>
          <div className="grid grid-cols-2 gap-1">
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                type="button"
                className={`cursor-pointer rounded-lg px-2.5 py-2 text-left transition-all duration-150 ${
                  lang === l.code
                    ? 'border border-de-gold bg-de-gold/15'
                    : 'border border-transparent bg-white/[0.03]'
                }`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
              >
                <p
                  className={`mb-0.5 text-[13px] font-bold ${
                    lang === l.code ? 'text-de-gold' : 'text-[#e8e0d0]'
                  }`}
                >
                  {l.native}
                </p>
                <p className="m-0 text-[9px] text-de-muted">{l.state}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
